import { GoalProgressDateRangePreset } from '@griply/types';
import { getGoalProgressDateRange } from '@griply/utils';
import { useGoalById, useGoalTasks, useSubGoals } from 'features/goals';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { PageSectionBase } from 'shared/components/ui/page-section-base';
import { useToday } from 'shared/contexts/today';
import { useUpdateGoalMutation } from 'shared/hooks/queries/use-update-goal-mutation';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes/paths';
import { DefaultMetricOptions } from 'shared/types/metric-template';
import { TaskType } from 'shared/types/task-base';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import { useTimeframeTimes } from '../../hooks/use-timeframe-times';
import { CompletionBanner } from '../completion-banner';
import { DateRangeSelect } from '../date-range-select';
import { ProgressCarousel } from '../progress-carousel';

export const ProgressSection: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const goal = useGoalById(id);
  const goalTasks = useGoalTasks(id);
  const subGoals = useSubGoals(id);
  const { mutate: updateGoal } = useUpdateGoalMutation();

  const goalDateRange = useTimeframeTimes(goal);
  const preset =
    goal?.progressDateRangePreset ?? GoalProgressDateRangePreset.Goal;

  const dateRange = useMemo(
    () =>
      getGoalProgressDateRange(preset, {
        goalRange: goalDateRange,
        today,
        weekStartsOn,
      }),
    [preset, goalDateRange, today, weekStartsOn],
  );

  const hasHabits = useMemo(
    () =>
      goalTasks.some(
        ({ type, archivedAt }) => type === TaskType.Habit && !archivedAt,
      ),
    [goalTasks],
  );

  const progressUrl = useMemo(
    () => replaceUrlParams(Paths.GoalDetailProgress, { id: id! }),
    [id],
  );

  const handleChangePreset = (next: GoalProgressDateRangePreset) => {
    if (!id) {
      return;
    }
    updateGoal({ goal: { id, progressDateRangePreset: next } });
  };

  if (
    !goal ||
    ((!goal.metric?.id ||
      goal.metric?.id === DefaultMetricOptions.CompletedTasks) &&
      !hasHabits &&
      !subGoals.length)
  ) {
    return null;
  }

  return (
    <PageSectionBase
      title={t('pages.goal-detail.progress.title')}
      url={progressUrl}
      interactions={
        <DateRangeSelect
          customRange={goalDateRange}
          onChange={handleChangePreset}
          value={preset}
        />
      }
      noPadding
    >
      {!!goal && <CompletionBanner goal={goal} />}
      <ProgressCarousel goal={goal} dateRange={dateRange} />
    </PageSectionBase>
  );
};
